import Button from '@components/atoms/button'
import CommonCarousel from '@components/common/carousel'
import { CalculatorIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { Code, MessageCircle } from 'react-feather'
import { ResponsiveType } from 'react-multi-carousel'

type Props = {
  handleBecomeTeacher: () => void
}

const TRACKS = [
  {
    icon: <Code className="text-orange" />,
    background: 'bg-orange-100',
    title: 'AI & Coding',
    description:
      'Teach students how to build with code through Scratch, Python, web development, game development, app development, and AI concepts.',
    learnMore: '/courses/',
  },
  {
    icon: <CalculatorIcon className="h-6 w-6 text-blue-200" />,
    background: 'bg-blue-50 bg-opacity-50',
    title: 'Math',
    description:
      'Teach math in a clear, engaging way, from core arithmetic to advanced concepts across grade levels.',
    learnMore: '/courses/math/',
  },
  {
    icon: <MessageCircle className="text-yellow-200" />,
    background: 'bg-yellow-100 bg-opacity-50',
    title: 'Public Speaking & Communication',
    description:
      'Help students build confidence, express ideas clearly, and become strong communicators.',
    learnMore: '/courses/public-speaking-for-kids/',
  },
]

const RESPONSIVE: ResponsiveType = {
  desktop: {
    breakpoint: { max: 4000, min: 1024 },
    items: 3,
  },
  tablet: {
    breakpoint: { max: 1023, min: 640 },
    items: 2,
  },
  mobile: {
    breakpoint: { max: 639, min: 0 },
    items: 1,
  },
}

const getBreakpointItems = (width: number): number => {
  if (width >= 1024) return RESPONSIVE.desktop.items as number
  if (width >= 640) return RESPONSIVE.tablet.items as number
  return RESPONSIVE.mobile.items as number
}

const TeachingTracks = ({ handleBecomeTeacher }: Props) => {
  const [showDots, setShowDots] = useState(false)

  useEffect(() => {
    const update = () =>
      setShowDots(TRACKS.length > getBreakpointItems(window.innerWidth))
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  return (
    <div className="py-12 lg:py-16 px-5 bg-white">
      <div className="container mx-auto flex flex-col items-center">
        <h2 className="max-w-4xl mx-auto pb-3 text-2xl text-center text-grey lg:text-4xl font-600">
          Explore our teaching tracks
        </h2>
        <p className="font-400 text-lg md:text-xl text-center max-w-4xl">
          Pick the track where you can teach with confidence and consistency. AI
          and Coding, Math, and Public Speaking &amp; Communication are open for
          K to 12 classrooms.
        </p>

        <div className="mt-10 w-full">
          <CommonCarousel
            responsive={RESPONSIVE}
            showDots={showDots}
            autoPlay={showDots}
            swipeable={showDots}
            draggable={showDots}
          >
            {TRACKS.map((track) => (
              <div
                key={track.title}
                className={`${track.background} rounded-xl p-4 lg:p-6 mx-2 flex flex-col items-start gap-4 border border-grey-300 h-full`}
              >
                <div
                  className={`${track.background} p-3 rounded-xl flex items-center justify-center shadow-inner`}
                >
                  {track.icon}
                </div>
                <h3 className="text-xl font-600 text-grey-900">
                  {track.title}
                </h3>
                <p className="text-grey-800 text-base font-400 flex-1">
                  {track.description}
                </p>
                <a
                  href={track.learnMore}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-orange hover:underline font-600"
                >
                  Learn more &rarr;
                </a>
              </div>
            ))}
          </CommonCarousel>
        </div>

        <div className="self-stretch flex justify-center mt-10">
          <Button
            variant="solid"
            intent="primary"
            size="lg"
            onClick={handleBecomeTeacher}
            className="w-full sm:w-auto"
          >
            Become a teacher
          </Button>
        </div>
      </div>
    </div>
  )
}

export default TeachingTracks
